import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon, MonitorIcon, WifiOffIcon, SparklesIcon, SmartphoneIcon, PinIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RichTextEditor } from '../RichTextEditor';
import { AttachmentPicker } from './AttachmentPicker';
import { HASHTAG_SUGGESTIONS, SCREEN_LOCATIONS, SCREEN_DURATIONS } from '../../data/communications';
import { getReach } from '../../utils/audience';
import { resolvedLength } from '../../utils/variables';
import { AiTips } from './AiTips';
import { generateForChannel, getChannelTips, isChannelEmpty, type AiContext, type AiTip } from '../../utils/aiAssist';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}
function Switch({
  checked,
  onChange,
  label
}: SwitchProps) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2 ${checked ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3926" data-mp="srhw6r2rn2da">
      <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3927" data-mp="a08mohszwdlp" />
    </button>;
}
interface ChannelEditorProps {
  channels: ChannelsState;
  onToggle: (key: keyof ChannelsState) => void;
  onUpdate: <K extends keyof ChannelsState>(key: K, patch: Partial<ChannelsState[K]>) => void;
  audienceTotal: number;
  /** Variables insertable in every channel's content. */
  variables: NoticeVariable[];
  /** Language the notice content is written in — drives resolved counters. */
  contentLang: 'fr' | 'en';
  /** Switches which language version of the content is being edited. */
  onContentLangChange: (value: 'fr' | 'en') => void;
  /** Whether each language version already holds content. */
  langStatus: {
    fr: boolean;
    en: boolean;
  };
  /** True when one version is written and the other is still empty. */
  canGenerateOtherLang: boolean;
  /** Writes the empty version from the one already completed. */
  onGenerateOtherLang: () => void;
  /** Everything the AI assistant needs to draft and review content. */
  aiContext: AiContext;
}
export function ChannelEditor({
  channels,
  onToggle,
  onUpdate,
  audienceTotal,
  variables,
  contentLang,
  onContentLangChange,
  langStatus,
  canGenerateOtherLang,
  onGenerateOtherLang,
  aiContext
}: ChannelEditorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  /** Tip whose target text is currently highlighted in an editor. */
  const [activeTip, setActiveTip] = useState<AiTip | null>(null);
  const selectTip = (tip: AiTip) => {
    if (!tip.highlight) return;
    setActiveTip(prev => prev?.id === tip.id ? null : tip);
  };

  /** Highlight to hand to a channel's main editor, if a tip points at it. */
  const highlightFor = (key: keyof ChannelsState) => activeTip && activeTip.id.startsWith(`${key}.`) ? activeTip.highlight : null;

  /** Drafts the whole channel — offered from its main editor while empty. */
  const generate = (key: keyof ChannelsState) => {
    onUpdate(key, generateForChannel(key, aiContext) as never);
    setActiveTip(null);
  };

  /** Generation handler for a channel's main editor, only while it is empty. */
  const generateFor = (key: keyof ChannelsState) => isChannelEmpty(key, channels) ? () => generate(key) : undefined;
  const card = (key: keyof ChannelsState, Icon: typeof MailIcon, title: string, subtitle: string, reachPercent: number, reachTone: 'good' | 'medium', body: React.ReactNode) => {
    const active = channels[key].active;
    const reach = getReach(audienceTotal, reachPercent, locale);
    return <div className="bg-white rounded-xl border-[0.5px] border-slate-200 overflow-hidden" data-id="element-3928" data-mp="i9btvt4f9mj1">
        <div className="flex items-center justify-between p-4 bg-white gap-4" data-id="element-3929" data-mp="n6k9bly6rcgl">
          <div className="flex items-center gap-4 min-w-0" data-id="element-3930" data-mp="s4vlw4fv812i">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-50 text-slate-400'}`} data-id="element-3931" data-mp="wx58bahruj01">
              <Icon className="w-5 h-5" data-id="element-3932" data-mp="15ymnmqncugno" />
            </div>
            <div className="min-w-0" data-id="element-3933" data-mp="1awo5x9cyiby3">
              <h3 className="text-base font-medium text-slate-900" data-id="element-3934" data-mp="1axpnzxse3ypb">{title}</h3>
              <p className="text-sm text-slate-500 truncate" data-id="element-3935" data-mp="2vf8furrbmd6j">{subtitle}</p>
            </div>
            <div className="ml-4 hidden md:flex items-center gap-2 flex-shrink-0" data-id="element-3936" data-mp="2qf475nwnmmqv">
              <span className="text-sm text-slate-600" data-id="element-3937" data-mp="2me0tn822x2st">
                {reach.count} {t('citoyens', 'citizens')}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${reachTone === 'good' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`} data-id="element-3938" data-mp="2hfz9skwtwzga">
                {reach.percent} % {t('de portée', 'reach')}
              </span>
            </div>
          </div>
          <Switch checked={active} onChange={() => onToggle(key)} label={title} data-id="element-3939" data-mp="30djox09skjnp" />
        </div>
        <AnimatePresence data-id="element-3940" data-mp="3t2qtafvxg3gv">
          {active && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-3941" data-mp="3t1612wx34wm6">
              <div className="p-6 pt-4 border-t border-slate-100 space-y-4 bg-slate-50/50" data-id="element-3942" data-mp="70i3y65x1u0l">
                {body}

                <AiTips tips={getChannelTips(key, aiContext)} activeTipId={activeTip?.id ?? null} onSelect={selectTip} data-id="element-3943" data-mp="b0iirrwxg786" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <section className="space-y-6" data-id="element-3944" data-mp="3k9mibwn30bxf">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-3945" data-mp="3o8lmvg4wi6oc">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-3946" data-mp="1ig6tn5ak1753">
          {t('Canaux de diffusion', 'Distribution channels')}
        </h2>

        {/* Language the notice CONTENT is written in — switches every channel
            at once, and is never the interface language. */}
        <div className="flex items-center gap-2" data-id="element-3947" data-mp="36xq5s0j9mr1a">
          <span className="text-sm font-medium text-slate-600" data-id="element-3948" data-mp="32z0k7ko2q36k">
            {t('Langue du contenu :', 'Content language:')}
          </span>
          <div role="group" aria-label={t('Langue du contenu', 'Content language')} className="inline-flex p-0.5 rounded-lg bg-slate-100 border border-slate-200" data-id="element-3949" data-mp="3gqx8teqj9uy7">
            {([{
            id: 'fr',
            label: 'FR',
            full: t('Français', 'French')
          }, {
            id: 'en',
            label: 'EN',
            full: t('Anglais', 'English')
          }] as const).map(l => <button key={l.id} type="button" onClick={() => onContentLangChange(l.id)} aria-pressed={contentLang === l.id} title={langStatus[l.id] ? l.full : `${l.full} — ${t('version vide', 'empty version')}`} className={`relative px-3 py-1.5 text-sm font-bold rounded-md transition-colors ${contentLang === l.id ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3950" data-mp="3uovl8elanhb2">
                {l.label}
                {!langStatus[l.id] && <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-3951" data-mp="wi5d2z3tb3p0" />}
              </button>)}
          </div>
        </div>
      </div>

      {/* One version is written, the other is untouched — offer to write it. */}
      {canGenerateOtherLang && <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-indigo-50/70 border border-indigo-100" data-id="element-3952" data-mp="2gvm5m2wzjw00">
          <p className="text-sm text-slate-700 min-w-0" data-id="element-3953" data-mp="2lxv23992hg6v">
            <span className="font-semibold text-slate-900" data-id="element-3954" data-mp="a8omsuky5u1d">
              {contentLang === 'fr' ? t('La version anglaise est vide.', 'The English version is empty.') : t('La version française est vide.', 'The French version is empty.')}
            </span>{' '}
            {t('ATNA peut la rédiger à partir du contenu déjà saisi.', 'ATNA can write it from the content already entered.')}
          </p>
          <button type="button" onClick={onGenerateOtherLang} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4F46E5] text-white text-sm font-semibold hover:bg-[#4338CA] transition-colors flex-shrink-0" data-id="element-3955" data-mp="3iohzeyc5qld5">
            <SparklesIcon className="w-4 h-4" data-id="element-3956" data-mp="38s55bzcpa5hv" />
            {contentLang === 'fr' ? t('Générer la version anglaise', 'Generate the English version') : t('Générer la version française', 'Generate the French version')}
          </button>
        </div>}
      <div className="space-y-4" data-id="element-3957" data-mp="2b3agd2xeb0bd">
        {/* EMAIL */}
        {card('email', MailIcon, t('Courriel', 'Email'), t("Lettre d'information détaillée", 'Detailed newsletter'), 82, 'good', <>
            <div data-id="element-3958" data-mp="2g3ydm6c7wde1">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3959" data-mp="2g5j8jx2g1ycf">
                {t('Objet du courriel', 'Email subject')}
              </label>
              <RichTextEditor value={channels.email.subject} onChange={subject => onUpdate('email', {
            subject
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t('Objet du courriel', 'Email subject')} placeholder={t('Objet…', 'Subject…')} data-id="element-3960" data-mp="1nfsrpg9fgicm" />
            </div>

            <div data-id="element-3961" data-mp="14o9tdmqclsgx">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3962" data-mp="14ot007ajsufn">
                {t('Contenu', 'Content')}
              </label>
              <RichTextEditor value={channels.email.body} onChange={body => onUpdate('email', {
            body
          })} minHeight={180} allowImages variables={variables} highlight={highlightFor('email')} onGenerate={generateFor('email')} ariaLabel={t('Contenu du courriel', 'Email content')} placeholder={t('Rédigez le contenu détaillé ici…', 'Write the detailed content here…')} data-id="element-3963" data-mp="3l2v2utr4d7fz" />
            </div>

            <AttachmentPicker attachments={channels.email.attachments} onChange={attachments => onUpdate('email', {
          attachments
        })} data-id="element-3964" data-mp="3u3jghnclp5sc" />
          </>)}

        {/* SMS */}
        {card('sms', MessageSquareIcon, t('SMS', 'SMS'), t('Message texte court et urgent', 'Short, urgent text message'), 95, 'good', <div data-id="element-3965" data-mp="k6kmvz22s7v9">
            <div className="flex justify-between items-end mb-1" data-id="element-3966" data-mp="k85h1nkh9osl">
              <label className="block text-sm font-medium text-slate-700" data-id="element-3967" data-mp="ffm32phvorn9">
                {t('Message SMS', 'SMS message')}
              </label>
              <span className={`text-xs ${resolvedLength(channels.sms.text, variables, contentLang) > 160 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3968" data-mp="aghrxtbe8jua">
                {resolvedLength(channels.sms.text, variables, contentLang)} / 160
              </span>
            </div>
            <RichTextEditor value={channels.sms.text} onChange={text => onUpdate('sms', {
          text
        })} minHeight={72} variablesOnly variables={variables} highlight={highlightFor('sms')} onGenerate={generateFor('sms')} ariaLabel={t('Message SMS', 'SMS message')} placeholder={t('Message texte court…', 'Short text message…')} data-id="element-3969" data-mp="3nounl9k28yi9" />
            <p className="mt-1.5 text-xs text-slate-400" data-id="element-3970" data-mp="235s6w0ler0nn">
              {t('Le compteur reflète la longueur finale, variables remplacées.', 'The counter reflects the final length, with variables replaced.')}
            </p>
          </div>)}

        {/* VOICE */}
        {card('voice', PhoneIcon, t('Voix automatisée', 'Automated voice'), t('Appel téléphonique généré par IA', 'AI-generated phone call'), 45, 'medium', <>
            <div data-id="element-3971" data-mp="1pff40cndic56">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3972" data-mp="1pbr8x92a5hoc">
                {t('Voix', 'Voice')}
              </label>
              <select value={channels.voice.voice} onChange={e => onUpdate('voice', {
            voice: e.target.value
          })} className="block w-full bg-white border border-slate-200 px-3 py-2 text-sm rounded-md focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-3973" data-mp="30shhgyu6ell4">
                <option value="femme" data-id="element-3974" data-mp="mmt3ewiub61j">
                  {t('Voix féminine (Standard)', 'Female voice (Standard)')}
                </option>
                <option value="homme" data-id="element-3975" data-mp="wjfj76wb76wp">{t('Voix masculine', 'Male voice')}</option>
              </select>
            </div>
            <div data-id="element-3976" data-mp="rjjhjeb33rkb">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3977" data-mp="rk30mych0lzx">
                {t("Script de l'appel", 'Call script')}
              </label>
              <RichTextEditor value={channels.voice.script} onChange={script => onUpdate('voice', {
            script
          })} minHeight={80} variablesOnly variables={variables} highlight={highlightFor('voice')} onGenerate={generateFor('voice')} ariaLabel={t("Script de l'appel", 'Call script')} placeholder={t('Bonjour, ceci est un message important de la ville…', 'Hello, this is an important message from the city…')} data-id="element-3978" data-mp="119vyyxnlr4m8" />
            </div>
          </>)}

        {/* PUSH */}
        {card('push', BellIcon, t('Notification Push', 'Push notification'), t("Alerte sur l'application mobile", 'Alert in the mobile app'), 88, 'good', <>
            <div data-id="element-3979" data-mp="118zwxa0mnqmt">
              <div className="flex justify-between items-end mb-1" data-id="element-3980" data-mp="119jbcgr2jww3">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3981" data-mp="166rykvr2qr47">
                  {t('Titre de la notification', 'Notification title')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.title, variables, contentLang) > 50 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3982" data-mp="w942a1dtlbjx">
                  {resolvedLength(channels.push.title, variables, contentLang)} / 50
                </span>
              </div>
              <RichTextEditor value={channels.push.title} onChange={title => onUpdate('push', {
            title
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('push')} onGenerate={generateFor('push')} ariaLabel={t('Titre de la notification', 'Notification title')} placeholder={t('Titre court…', 'Short title…')} data-id="element-3983" data-mp="2k6wcramlr9nk" />
            </div>
            <div data-id="element-3984" data-mp="1wi3g1by5oxrw">
              <div className="flex justify-between items-end mb-1" data-id="element-3985" data-mp="1wimz4vzjls7i">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3986" data-mp="1senlcl5nd7hu">
                  {t('Message', 'Message')}
                </label>
                <span className={`text-xs ${resolvedLength(channels.push.message, variables, contentLang) > 100 ? 'text-red-500 font-medium' : 'text-slate-400'}`} data-id="element-3987" data-mp="7vl8jna47tsi">
                  {resolvedLength(channels.push.message, variables, contentLang)} / 100
                </span>
              </div>
              <RichTextEditor value={channels.push.message} onChange={message => onUpdate('push', {
            message
          })} minHeight={56} variablesOnly variables={variables} ariaLabel={t('Message', 'Message')} placeholder={t("Détails de l'alerte…", 'Alert details…')} data-id="element-3988" data-mp="vn8qhfpefuin" />
            </div>
          </>)}

        {/* WEBSITE */}
        {card('website', GlobeIcon, t('Site web', 'Website'), t("Bannière d'alerte sur le portail", 'Alert banner on the portal'), 100, 'good', <>
            <div data-id="element-3989" data-mp="185m9u1snluz3">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3990" data-mp="1841o91obhmdd">
                {t('Texte de la bannière', 'Banner text')}
              </label>
              <RichTextEditor value={channels.website.bannerText} onChange={bannerText => onUpdate('website', {
            bannerText
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('website')} onGenerate={generateFor('website')} ariaLabel={t('Texte de la bannière', 'Banner text')} placeholder={t('Alerte importante…', 'Important alert…')} data-id="element-3991" data-mp="1gzhdt6i1hfd5" />
            </div>
            <div data-id="element-3992" data-mp="29nda4a3p559e">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3993" data-mp="29lsf6jdgzkb0">
                {t("Label du bouton d'action", 'Action button label')}
              </label>
              <RichTextEditor value={channels.website.ctaLabel} onChange={ctaLabel => onUpdate('website', {
            ctaLabel
          })} minHeight={40} variablesOnly singleLine variables={variables} ariaLabel={t("Label du bouton d'action", 'Action button label')} placeholder={t('En savoir plus', 'Learn more')} data-id="element-3994" data-mp="dxatk2nbgwg" />
            </div>
          </>)}

        {/* SOCIAL */}
        {card('social', Share2Icon, t('Réseaux sociaux', 'Social media'), t('Publication Facebook et X', 'Facebook and X post'), 60, 'good', <>
            <div data-id="element-3995" data-mp="y0we4dnufn3b">
              <div className="flex justify-between items-end mb-1" data-id="element-3996" data-mp="y0cwky23v0pt">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3997" data-mp="12sw87ph8flrz">
                  {t('Texte de la publication', 'Post text')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-3998" data-mp="1j4ho77sjt8j6">
                  {resolvedLength(channels.social.postText, variables, contentLang)}{' '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.social.postText} onChange={postText => onUpdate('social', {
            postText
          })} minHeight={130} allowImages variables={variables} highlight={highlightFor('social')} onGenerate={generateFor('social')} ariaLabel={t('Texte de la publication', 'Post text')} placeholder={t('Quoi de neuf ?', "What's new?")} data-id="element-3999" data-mp="1ean8sgjz7fpw" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-4000" data-mp="2lq1ntppvne97">
                {t('Les publications avec image obtiennent en moyenne 2× plus d’interactions.', 'Posts with an image get on average 2× more engagement.')}
              </p>
            </div>

            <div data-id="element-4001" data-mp="a2y5kf1bulhx">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4002" data-mp="a3honz2prfxj">
                {t('Suggestions de hashtags', 'Hashtag suggestions')}
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-4003" data-mp="j1a4r5g9erlz">
                {HASHTAG_SUGGESTIONS.map(tag => <button key={tag} type="button" onClick={() => onUpdate('social', {
              postText: `${channels.social.postText}${channels.social.postText ? ' ' : ''}${tag}`
            })} className="px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-600 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-4004" data-mp="xpwi51mbt9ak">
                    {tag}
                  </button>)}
              </div>
            </div>
          </>)}

        {/* PUBLIC SCREENS */}
        {card('screens', MonitorIcon, t('Écrans publics', 'Public screens'), t('Affichage dans les bâtiments municipaux', 'Signage in municipal buildings'), 35, 'medium', <>
            <div data-id="element-4005" data-mp="1qfdrnzoo4t1x">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4006" data-mp="1qdswq8yfz83j">
                {t('Titre affiché', 'Displayed headline')}
              </label>
              <RichTextEditor value={channels.screens.headline} onChange={headline => onUpdate('screens', {
            headline
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('screens')} onGenerate={generateFor('screens')} ariaLabel={t('Titre affiché', 'Displayed headline')} placeholder={t('Avis important', 'Important notice')} data-id="element-4007" data-mp="2071f7pi3tpy4" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-4008" data-mp="3tqsabua2bq65">
                {t('Lisible à 3 mètres : visez moins de 8 mots.', 'Readable from 3 metres: aim for fewer than 8 words.')}
              </p>
            </div>

            <div data-id="element-4009" data-mp="l84ajr8gezx9">
              <div className="flex justify-between items-end mb-1" data-id="element-4010" data-mp="laqcnbcksn83">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4011" data-mp="h8bvcvok1vix">
                  {t('Message', 'Message')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-4012" data-mp="7h8w7zf6rqma">
                  {resolvedLength(channels.screens.bodyText, variables, contentLang)}
                  {' / 180 '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.screens.bodyText} onChange={bodyText => onUpdate('screens', {
            bodyText
          })} minHeight={80} variablesOnly variables={variables} ariaLabel={t('Message', 'Message')} placeholder={t('Une ou deux phrases courtes…', 'One or two short sentences…')} data-id="element-4013" data-mp="jpm3sds2xt7v" />
            </div>

            <div data-id="element-4014" data-mp="17ewvz6vd6fae">
              <label htmlFor="screen-duration" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4015" data-mp="17fgf2qwr39q0">
                {t('Durée d’affichage', 'Slide duration')}
              </label>
              <select id="screen-duration" value={channels.screens.duration} onChange={e => onUpdate('screens', {
            duration: e.target.value
          })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4016" data-mp="ylbidquxr42k">
                {SCREEN_DURATIONS.map(option => <option key={option.value} value={option.value} data-id="element-4017" data-mp="2a7soig4i62md">
                    {option.label[lang]}
                  </option>)}
              </select>
            </div>

            <div data-id="element-4018" data-mp="1hkexeynhk1a6">
              <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap" data-id="element-4019" data-mp="1hlzizyrto9vw">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4020" data-mp="1dk4hat5zbuhl">
                  {t('Écrans ciblés', 'Target screens')}
                </label>
                <span className="text-xs text-slate-500" data-id="element-4021" data-mp="1miq799ds6bse">
                  {(() => {
                const views = SCREEN_LOCATIONS.filter(s => s.online && channels.screens.locations.includes(s.id)).reduce((sum, s) => sum + s.dailyViews, 0);
                return t(`≈ ${views.toLocaleString(locale)} passages par jour`, `≈ ${views.toLocaleString(locale)} passers-by per day`);
              })()}
                </span>
              </div>
              <div className="space-y-2" data-id="element-4022" data-mp="3jz5evonph841">
                {SCREEN_LOCATIONS.map(screen => {
              const checked = channels.screens.locations.includes(screen.id);
              return <label key={screen.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white cursor-pointer transition-colors ${checked ? 'border-[#4F46E5] ring-1 ring-[#4F46E5]/20' : 'border-slate-200 hover:bg-slate-50'} ${screen.online ? '' : 'opacity-60'}`} data-id="element-4023" data-mp="2r96n3li4mwvo">
                      <input type="checkbox" checked={checked} disabled={!screen.online} onChange={() => onUpdate('screens', {
                  locations: checked ? channels.screens.locations.filter(id => id !== screen.id) : [...channels.screens.locations, screen.id]
                })} className="w-4 h-4 rounded border-slate-300 text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4024" data-mp="28hdv9uesnhpj" />
                      <span className="min-w-0 flex-1" data-id="element-4025" data-mp="13c78gi6rm3ve">
                        <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-4026" data-mp="13dqoq8sf8ex6">
                          {screen.place[lang]}
                        </span>
                        <span className="block text-xs text-slate-500 truncate" data-id="element-4027" data-mp="ub90jot7hc7j">
                          {screen.label[lang]}
                          {' · '}
                          {screen.dailyViews.toLocaleString(locale)}{' '}
                          {t('passages/jour', 'views/day')}
                        </span>
                      </span>
                      {!screen.online && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium flex-shrink-0" data-id="element-4028" data-mp="2d8ayxri25zsh">
                          <WifiOffIcon className="w-3 h-3" data-id="element-4029" data-mp="248fk9cpanmnp" />
                          {t('hors ligne', 'offline')}
                        </span>}
                    </label>;
            })}
              </div>
            </div>
          </>)}

        {/* CITIZEN APP */}
        {card('app', SmartphoneIcon, t('Application', 'App'), t('Affichage dans le fil de l’application citoyenne', 'Displayed in the citizen app feed'), 62, 'good', <>
            <div data-id="element-4030" data-mp="wx1ih8i92y3z">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4031" data-mp="wxkygjocbcfd">
                {t('Titre', 'Title')}
              </label>
              <RichTextEditor value={channels.app.title} onChange={title => onUpdate('app', {
            title
          })} minHeight={40} variablesOnly singleLine variables={variables} highlight={highlightFor('app')} onGenerate={generateFor('app')} ariaLabel={t('Titre dans l’application', 'App title')} placeholder={t('Titre de l’avis', 'Notice title')} data-id="element-4032" data-mp="1aydw3d9xunmp" />
            </div>

            <div data-id="element-4033" data-mp="1po333p4ppluw">
              <div className="flex justify-between items-end mb-1" data-id="element-4034" data-mp="23l8c9xzq30ud">
                <label className="block text-sm font-medium text-slate-700" data-id="element-4035" data-mp="23jdp0d0zawn4">
                  {t('Résumé', 'Summary')}
                </label>
                <span className="text-xs text-slate-400" data-id="element-4036" data-mp="1r3rqsvrgm8be">
                  {resolvedLength(channels.app.summary, variables, contentLang)}
                  {' / 160 '}
                  {t('caractères', 'characters')}
                </span>
              </div>
              <RichTextEditor value={channels.app.summary} onChange={summary => onUpdate('app', {
            summary
          })} minHeight={60} variablesOnly variables={variables} ariaLabel={t('Résumé dans l’application', 'App summary')} placeholder={t('Une ou deux phrases affichées sur la carte du fil…', 'One or two sentences shown on the feed card…')} data-id="element-4037" data-mp="1i4ms9pfweez8" />
              <p className="mt-1.5 text-xs text-slate-400" data-id="element-4038" data-mp="pduwgt2qvscf">
                {t('Version courte visible dans le fil, avant que le citoyen ouvre l’avis.', 'Short version shown in the feed, before citizens open the notice.')}
              </p>
            </div>

            <div data-id="element-4039" data-mp="f9eiy6cqmnbs">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4040" data-mp="f8wdqk83kyrq">
                {t('Contenu', 'Content')}
              </label>
              <RichTextEditor value={channels.app.body} onChange={body => onUpdate('app', {
            body
          })} minHeight={140} variables={variables} ariaLabel={t('Contenu dans l’application', 'App content')} placeholder={t('Le détail affiché quand le citoyen ouvre l’avis…', 'The details shown when citizens open the notice…')} data-id="element-4041" data-mp="k7fzajis48jk" />
            </div>

            <label className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors" data-id="element-4042" data-mp="34zj8f36c92ir">
              <input type="checkbox" checked={channels.app.pinned} onChange={e => onUpdate('app', {
            pinned: e.target.checked
          })} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#4F46E5] focus:ring-[#4F46E5]" data-id="element-4043" data-mp="39x1hquo9pjra" />
              <span className="min-w-0" data-id="element-4044" data-mp="3bn6m5w4o43k0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800" data-id="element-4045" data-mp="36owuhvi5jl35">
                  <PinIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-4046" data-mp="2iysqhn11xjbm" />
                  {t('Épingler en tête du fil', 'Pin to the top of the feed')}
                </span>
                <span className="block text-xs text-slate-500" data-id="element-4047" data-mp="2e22bl30zgtrs">
                  {t('L’avis reste au-dessus des autres publications jusqu’à sa date de fin.', 'The notice stays above other posts until its end date.')}
                </span>
              </span>
            </label>
          </>)}
      </div>
    </section>;
}